import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { inventoryApi, type Warehouse } from '../../api/inventory';

export function WarehouseDashboardPage() {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    void inventoryApi.listWarehouses().then(setWarehouses).catch((e) => setError(String(e)));
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Warehouse Dashboard</h1>
        <Link to="/inventory" className="text-sm text-slate-600 hover:underline">Back to hub</Link>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {warehouses.map((w) => (
          <div key={w.id} className="rounded-xl border bg-white p-4">
            <p className="font-medium">{w.name}</p>
            <p className="text-sm text-slate-500">{w.code}</p>
            <p className="mt-2 text-xs uppercase tracking-wide text-slate-400">{w.status}</p>
            {w.address && <p className="mt-2 text-sm text-slate-600">{w.address}</p>}
          </div>
        ))}
        {warehouses.length === 0 && !error && <p className="text-sm text-slate-500">No warehouses yet.</p>}
      </div>
    </div>
  );
}

export function WarehouseManagerPage() {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const reload = () => void inventoryApi.listWarehouses().then(setWarehouses).catch((e) => setError(String(e)));

  useEffect(() => { reload(); }, []);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      await inventoryApi.createWarehouse({ code, name });
      setCode('');
      setName('');
      setMessage('Warehouse created.');
      reload();
    } catch (err) {
      setError(String(err));
    }
  };

  const onDelete = async (id: string) => {
    setError('');
    try {
      await inventoryApi.deleteWarehouse(id);
      reload();
    } catch (err) {
      setError(String(err));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Warehouse Manager</h1>
      <form onSubmit={onCreate} className="mt-6 flex flex-wrap gap-3 rounded-xl border bg-white p-4">
        <input className="rounded border px-3 py-2 text-sm" placeholder="Code" value={code} onChange={(e) => setCode(e.target.value)} required />
        <input className="rounded border px-3 py-2 text-sm" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <button type="submit" className="rounded bg-slate-900 px-4 py-2 text-sm text-white">Create warehouse</button>
      </form>
      {message && <p className="mt-4 text-sm text-green-700">{message}</p>}
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <table className="mt-6 w-full text-left text-sm">
        <thead><tr className="border-b"><th className="py-2">Code</th><th>Name</th><th>Status</th><th /></tr></thead>
        <tbody>
          {warehouses.map((w) => (
            <tr key={w.id} className="border-b">
              <td className="py-2">{w.code}</td>
              <td>{w.name}</td>
              <td>{w.status}</td>
              <td><button type="button" className="text-red-600" onClick={() => void onDelete(w.id)}>Delete</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
